'use client';

import { Icon, IconProps } from '@/components/LucideIcon';
import { Slot, Slottable } from '@radix-ui/react-slot';
import * as stylex from '@stylexjs/stylex';
import React, { useMemo } from 'react';
import { a11y } from '../../styles/a11y';
import '../../styles/tokens.stylex';
import { spacing } from '../../styles/tokens.stylex';
import { typography } from '../../styles/typography';
import './buttonThemes.stylex';
import { buttonSizes, buttonSizesCompact, buttonSizesMediaAction, buttonSizesMediaActionLarge, buttonSizesXCompact, buttonTheme, cardLinkButtonTheme, ghostButtonTheme, mediaActionButtonTheme, mediaActionLargeButtonTheme, primaryGhostButtonTheme, secondarySolidButtonTheme } from './buttonThemes.stylex';
import { Loader } from '../Loader';
const MEDIA_HOVER = '@media (hover: hover) and (pointer: fine)';
type IconName = IconProps['name'];
export type ButtonProps = {
  styles?: stylex.StyleXStyles;
  iconStyles?: stylex.StyleXStyles;
  asChild?: boolean;
  href?: string;
  children?: React.ReactNode;
  secondaryText?: string | React.ReactNode;
  isActive?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
  outdentLeftPadding?: boolean;
  outdentRightPadding?: boolean;
  centerTextBlock?: boolean;
  leftAlign?: boolean;
  rightAlign?: boolean;
  controlled?: boolean;
  iconLeft?: React.ReactNode | IconName;
  iconRight?: React.ReactNode | IconName;
  iconOnly?: React.ReactNode | IconName | boolean;
  iconLeftStyles?: stylex.StyleXStyles;
  iconRightStyles?: stylex.StyleXStyles;
  iconOnlyStyles?: stylex.StyleXStyles;
  gatherContent?: boolean;
  stopPropagation?: boolean;
  hoverTrigger?: boolean;
  'data-tina-field'?: string;
} & ButtonVariantProps & SpacerOverRides;
type ButtonVariantProps = {
  theme?: 'primary' | 'secondary' | 'neutral' | 'success';
  variant?: 'solid' | 'ghost' | 'link' | 'ui' | 'media-action';
  size?: 'regular' | 'large' | 'compact' | 'extra-compact';
};
type SpacerOverRides = {
  spacerStyle?: stylex.StyleXStyles;
};
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps & React.ComponentProps<'button'>>(function Button({
  asChild,
  centerTextBlock,
  children,
  controlled,
  gatherContent,
  hoverTrigger,
  iconLeft,
  iconRight,
  iconOnly,
  iconLeftStyles,
  iconRightStyles,
  iconOnlyStyles,
  leftAlign,
  rightAlign,
  outdentLeftPadding,
  outdentRightPadding,
  theme = 'primary',
  variant = 'solid',
  size = 'regular',
  stopPropagation,
  styles,
  secondaryText,
  isLoading,
  loadingLabel = 'Loading',
  ...props
}, ref) {
  const Component = asChild ? Slot : 'button';
  void gatherContent;
  void stopPropagation;
  function setButtonIcon(icon: string | React.ReactNode, iconStyles?: stylex.StyleXStyles) {
    if (!icon || typeof icon === 'boolean') return null;
    if (typeof icon === 'string') {
      return <Icon name={icon as IconName} styles={[sx.lucideIcon, iconStyles]} />;
    }
    if (React.isValidElement(icon)) return icon;
    return null;
  }
  let leftIconComponent = useMemo(() => setButtonIcon(iconLeft || iconOnly, iconLeftStyles || iconOnlyStyles), [iconLeft, iconOnly, iconLeftStyles, iconOnlyStyles]);
  let rightIconComponent = useMemo(() => setButtonIcon(iconRight, iconRightStyles), [iconRight, iconRightStyles]);
  if (centerTextBlock && !iconOnly && !leftAlign && !rightAlign) {
    const spacer = <IconSpacer spacerStyle={props.spacerStyle} />;
    leftIconComponent = leftIconComponent || rightIconComponent && spacer || null;
    rightIconComponent = rightIconComponent || leftIconComponent && spacer || null;
  }
  const themedStyles = useMemo(() => getStylesFromVariants({
    size,
    theme,
    variant
  }), [size, theme, variant]);
  const loadingComponent = useMemo(() => {
    return isLoading && !asChild ? <span className="x10a8y8t x6s0dn4 x78zum5 xl56j7k x1hc1fzr x47corl x10l6tqk">
        <Loader size="1.5em" styles={sx.loadingIcon} />
      </span> : null;
  }, [asChild, isLoading]);
  const buttonContent = <>
      {leftIconComponent}
      {!!iconLeft && <PaddingSpacer />}
      {children && !iconOnly && <Slottable>
          {secondaryText ? <span className="xt42msi x78zum5 x1t1x2f9 xdt5ytf x1iyjqo2 xs83m0k">
              <span {...stylex.props(sx.textWrapper, !!iconOnly && a11y.visuallyHidden)}>
                {children}
              </span>
              <span {...stylex.props(typography.small, sx.secondaryText)}>
                {secondaryText}
              </span>
            </span> : <span {...stylex.props(sx.textWrapper, !!iconOnly && a11y.visuallyHidden)}>
              {children}
            </span>}
        </Slottable>}
      {!!iconRight && <PaddingSpacer />}
      {rightIconComponent}
    </>;
  const visibleButtonContent = <span {...{
    0: {
      className: "x1myia39 x6s0dn4 x78zum5 x1hc1fzr xh8yej3 xl56j7k"
    },
    1: {
      className: "x1myia39 x6s0dn4 x78zum5 xh8yej3 xl56j7k xg01cxk"
    }
  }[!!isLoading << 0]}>
      {buttonContent}
    </span>;
  if (asChild) {
    return <Component {...stylex.props(a11y.focusRing, typography.label, sx.buttonBaseStyles, hoverTrigger ? sx.buttonHoverTriggerStyles : sx.buttonHoverStyles, outdentLeftPadding && sx.shiftButtonLeft, outdentRightPadding && sx.shiftButtonRight, themedStyles, !!iconOnly && sx.iconOnlyStyles, leftAlign && sx.leftAlign, rightAlign && sx.rightAlign, controlled && sx.controlledStyles, props.disabled && [sx.disabledStyles, sx.disableClicks], isLoading && sx.disableClicks, styles)} type="button" {...props} ref={ref}>
        {leftIconComponent}
        {!!iconLeft && <PaddingSpacer />}
        {children && !iconOnly && <Slottable>
            {secondaryText ? <span className="xt42msi x78zum5 x1t1x2f9 xdt5ytf x1iyjqo2 xs83m0k">
                <span {...stylex.props(sx.textWrapper, !!iconOnly && a11y.visuallyHidden)}>
                  {children}
                </span>
                <span {...stylex.props(typography.small, sx.secondaryText)}>
                  {secondaryText}
                </span>
              </span> : <span {...stylex.props(sx.textWrapper, !!iconOnly && a11y.visuallyHidden)}>
                {children}
              </span>}
          </Slottable>}
        {!!iconRight && <PaddingSpacer />}
        {rightIconComponent}
      </Component>;
  }
  return <Component {...stylex.props(a11y.focusRing, typography.label, sx.buttonBaseStyles, hoverTrigger ? sx.buttonHoverTriggerStyles : sx.buttonHoverStyles, outdentLeftPadding && sx.shiftButtonLeft, outdentRightPadding && sx.shiftButtonRight, themedStyles, !!iconOnly && sx.iconOnlyStyles, leftAlign && sx.leftAlign, rightAlign && sx.rightAlign, controlled && sx.controlledStyles, props.disabled && sx.disabledStyles, styles)} type="button" {...props} aria-busy={isLoading || undefined} aria-label={isLoading ? loadingLabel : props['aria-label']} ref={ref}>
      {loadingComponent}
      {visibleButtonContent}
    </Component>;
});
export const sx = {
  lucideIcon: {
    ku685b: "x1phtjf2",
    kmuXW: "x2lah0s",
    k1K539: "x15qqh2w",
    kKMj4B: "xkq7u5t",
    kZKoxP: "xwcisui",
    kzqmXN: "xwdhrc6",
    $$css: true
  },
  paddingSpacer: {
    kzQI83: "x1c4vz4f",
    kmuXW: "x2lah0s",
    kZKoxP: "xae2q98",
    kzqmXN: "xqpsc9y",
    $$css: true
  },
  shiftButtonLeft: {
    koQZXg: "x1ab47tp",
    $$css: true
  },
  shiftButtonRight: {
    km5ZXQ: "x1cezy9i",
    $$css: true
  },
  iconSpacer: {
    kB7OPa: "x9f619",
    kCS8Yb: "x4kk4q9",
    kmuXW: "x1oc8zis",
    kZKoxP: "xae2q98",
    $$css: true
  },
  iconOnlyStyles: {
    kmVPX3: "x1717udv",
    kOBAk4: "x1y5e3q9",
    kzQI83: "x1c4vz4f",
    kjj79g: "xl56j7k",
    $$css: true
  },
  buttonHoverStyles: {
    kVAM5u: "x1lja4gf x1u1pvjy xofcv7z",
    kWkggS: "x1worloh x1fbcuw5 xpiz9hr",
    kKwaWg: "xv3rylr x1p9kgqz xv0biy7",
    ku685b: "xkkl5bb",
    $$css: true
  },
  buttonHoverTriggerStyles: {
    kVAM5u: "x1lja4gf x1u1pvjy xtx3d0l x1eam967 xofcv7z",
    kWkggS: "x1worloh x1fbcuw5 x1kfscpz x1c92o4h xpiz9hr",
    kKwaWg: "xv3rylr x1p9kgqz xciwjpa x1uwvogq xv0biy7",
    ku685b: "x8tpeuw xkkl5bb",
    $$css: true
  },
  buttonBaseStyles: {
    kaIpWk: "xc52o87",
    ksu8eU: "xnr5sdx",
    kMzoRj: "xqfhbbh",
    k8WAf4: "x1v68zcs",
    kg3NbH: "xmvrule",
    kybGjl: "x1hl2dhg x1lku1pv",
    kmkexE: "x54ks1",
    kGNEyG: "x6s0dn4",
    kGVxlE: "x14u4rpm",
    kB7OPa: "x9f619",
    kMwMTN: "xvnj79d",
    kkrTdU: "x1277o0a",
    k1xSpc: "x78zum5",
    kGuDYH: "x6yob9d",
    k63SB2: "x17q3fyn",
    kVAEAm: "x1n2onr6",
    k6sLGO: "x1oc7514",
    kAzted: "x1njf6cm",
    $$css: true
  },
  disabledStyles: {
    ku685b: "xkcp37y",
    kSiTet: "xbyyjgo",
    $$css: true
  },
  disableClicks: {
    kkrTdU: "xt0e3qv",
    kfzvcC: "x47corl",
    $$css: true
  },
  contentWrapper: {
    kmkexE: "x1myia39",
    kGNEyG: "x6s0dn4",
    k1xSpc: "x78zum5",
    kSiTet: "x1hc1fzr",
    kzqmXN: "xh8yej3",
    kjj79g: "xl56j7k",
    $$css: true
  },
  loadingContent: {
    kSiTet: "xg01cxk",
    $$css: true
  },
  loadingWrapper: {
    kpwlN0: "x10a8y8t",
    kGNEyG: "x6s0dn4",
    k1xSpc: "x78zum5",
    kjj79g: "xl56j7k",
    kSiTet: "x1hc1fzr",
    kfzvcC: "x47corl",
    kVAEAm: "x10l6tqk",
    $$css: true
  },
  textWrapper: {
    kCS8Yb: "x4y2jaj",
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kKMj4B: "xkq7u5t",
    $$css: true
  },
  controlledStyles: {
    kfzvcC: "x47corl",
    $$css: true
  },
  leftAlign: {
    k9WMMc: "xdpxx8g",
    $$css: true
  },
  rightAlign: {
    k9WMMc: "x1hr2gdg",
    $$css: true
  },
  boldText: {
    k63SB2: "x117nqv4",
    $$css: true
  },
  secondaryTextWrapper: {
    kqGvvJ: "xt42msi",
    k1xSpc: "x78zum5",
    kCS8Yb: "x1t1x2f9",
    kXwgrk: "xdt5ytf",
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    $$css: true
  },
  secondaryText: {
    kMwMTN: "x15rks2t",
    $$css: true
  },
  loadingIcon: {
    kMwMTN: "xvnj79d",
    $$css: true
  }
};
function getStylesFromVariants({
  size,
  theme,
  variant
}: ButtonVariantProps) {
  const styles = [];
  if (size === 'compact') {
    styles.push(buttonSizesCompact);
  }
  if (size === 'extra-compact') {
    styles.push(buttonSizesXCompact);
  }
  switch (variant) {
    case 'solid':
      if (theme === 'secondary') {
        styles.push(secondarySolidButtonTheme);
      }
      break;
    case 'ghost':
      if (theme === 'primary') {
        styles.push(primaryGhostButtonTheme);
      }
      if (theme === 'neutral') {
        styles.push(ghostButtonTheme);
      }
      break;
    case 'link':
      styles.push(cardLinkButtonTheme);
      break;
    case 'media-action':
      if (size === 'regular') {
        styles.push(buttonSizesMediaAction);
        styles.push(mediaActionButtonTheme);
      }
      if (size === 'large') {
        styles.push(buttonSizesMediaActionLarge);
        styles.push(mediaActionLargeButtonTheme);
      }
      break;
    default:
      console.warn(`${variant} button type not recognised`);
      break;
  }
  return styles;
}
function IconSpacer({
  spacerStyle
}: SpacerOverRides) {
  return <span {...stylex.props(sx.iconSpacer, spacerStyle)} />;
}
function PaddingSpacer() {
  return <span className="x1c4vz4f x2lah0s xae2q98 xqpsc9y" />;
}